import { useEffect, useMemo, useState } from 'react'
import { extractApiErrorMessage } from '../../../../utils/apiError'
import {
  downloadTaskAnalyticsExport,
  fetchTaskAnalyticsDrilldown,
  fetchTaskAnalyticsFilters,
  fetchTaskAnalyticsInsights,
  fetchTaskAnalyticsRiskExport,
  fetchTaskAnalyticsRiskList,
  fetchTaskAnalyticsTeamLoad,
} from '../../api/analyticsApi'

function todayIso() {
  return new Date().toISOString().slice(0, 10)
}

function daysAgoIso(days) {
  const current = new Date()
  current.setDate(current.getDate() - Math.max(0, Number(days || 0)))
  return current.toISOString().slice(0, 10)
}

function toDropdownOptions(items = [], emptyLabel = 'All') {
  return [{ value: '', label: emptyLabel }, ...items.map((item) => ({ value: String(item.id), label: item.name }))]
}

function toValueOptions(items = [], emptyLabel = 'All') {
  return [{ value: '', label: emptyLabel }, ...items.map((item) => ({ value: String(item), label: String(item).replaceAll('_', ' ') }))]
}

function downloadCsv(filename, rows = []) {
  const csv = rows.map((columns) => columns.map((value) => `"${String(value ?? '').replaceAll('"', '""')}"`).join(',')).join('\n')
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
  const link = document.createElement('a')
  link.href = URL.createObjectURL(blob)
  link.download = filename
  link.click()
  URL.revokeObjectURL(link.href)
}

export function useTaskAnalytics() {
  const [projectId, setProjectId] = useState('')
  const [departmentId, setDepartmentId] = useState('')
  const [userId, setUserId] = useState('')
  const [status, setStatus] = useState('')
  const [priority, setPriority] = useState('')
  const [slaStatus, setSlaStatus] = useState('')
  const [presetDays, setPresetDays] = useState('30')
  const [dateFrom, setDateFrom] = useState(daysAgoIso(29))
  const [dateTo, setDateTo] = useState(todayIso())

  const [projects, setProjects] = useState([])
  const [departments, setDepartments] = useState([])
  const [datePresets, setDatePresets] = useState([])
  const [users, setUsers] = useState([])
  const [statuses, setStatuses] = useState([])
  const [priorities, setPriorities] = useState([])
  const [slaStatuses, setSlaStatuses] = useState([])
  const [insights, setInsights] = useState(null)
  const [riskRows, setRiskRows] = useState([])
  const [riskCounts, setRiskCounts] = useState({})
  const [riskPage, setRiskPage] = useState(1)
  const [riskPageSize] = useState(5)
  const [riskTotal, setRiskTotal] = useState(0)
  const [isLoadingRisk, setIsLoadingRisk] = useState(true)
  const [teamLoad, setTeamLoad] = useState(null)

  // View toggles
  const [delayTrendView, setDelayTrendView] = useState('chart')
  const [taskLoadView, setTaskLoadView] = useState('chart')

  // Drilldown state
  const [isDetailsOpen, setIsDetailsOpen] = useState(false)
  const [drillTitle, setDrillTitle] = useState('Task Details')
  const [drillLevel, setDrillLevel] = useState('task')
  const [drillPeriodFrom, setDrillPeriodFrom] = useState(null)
  const [drillPeriodTo, setDrillPeriodTo] = useState(null)
  const [drillStatus, setDrillStatus] = useState(null)
  const [drillUserId, setDrillUserId] = useState(null)
  const [drillPriority, setDrillPriority] = useState(null)
  const [drilldown, setDrilldown] = useState({ columns: [], rows: [] })
  const [isLoadingDrilldown, setIsLoadingDrilldown] = useState(false)
  const [drilldownError, setDrilldownError] = useState(null)

  const [isLoadingFilters, setIsLoadingFilters] = useState(true)
  const [isLoadingInsights, setIsLoadingInsights] = useState(true)
  const [isLoadingTeam, setIsLoadingTeam] = useState(false)
  const [isExporting, setIsExporting] = useState(false)
  const [isTeamOpen, setIsTeamOpen] = useState(false)
  const [error, setError] = useState('')
  const [teamError, setTeamError] = useState('')

  useEffect(() => {
    let active = true
    async function loadFilters() {
      setIsLoadingFilters(true)
      try {
        const payload = await fetchTaskAnalyticsFilters()
        if (!active) return
        setProjects(Array.isArray(payload?.projects) ? payload.projects : [])
        setDepartments(Array.isArray(payload?.departments) ? payload.departments : [])
        setDatePresets(Array.isArray(payload?.datePresets) ? payload.datePresets : [])
        setUsers(Array.isArray(payload?.users) ? payload.users : [])
        setStatuses(Array.isArray(payload?.statuses) ? payload.statuses : [])
        setPriorities(Array.isArray(payload?.priorities) ? payload.priorities : [])
        setSlaStatuses(Array.isArray(payload?.slaStatuses) ? payload.slaStatuses : [])
      } catch (requestError) {
        if (!active) return
        setProjects([])
        setDepartments([])
        setDatePresets([])
        setError(extractApiErrorMessage(requestError, 'Failed to load task analytics filters.'))
      } finally {
        if (active) setIsLoadingFilters(false)
      }
    }
    loadFilters()
    return () => {
      active = false
    }
  }, [])

  useEffect(() => {
    if (!projectId) return
    const isValid = projects.some((item) => String(item.id) === String(projectId))
    if (!isValid) {
      setProjectId('')
    }
  }, [projectId, projects])

  useEffect(() => {
    setRiskPage(1)
  }, [projectId, departmentId, userId, status, priority, slaStatus, dateFrom, dateTo])

  useEffect(() => {
    let active = true
    async function loadInsights() {
      setIsLoadingInsights(true)
      setError('')
      try {
        const query = {
          projectId: projectId || undefined,
          departmentId: departmentId || undefined,
          userId: userId || undefined,
          status: status || undefined,
          priority: priority || undefined,
          slaStatus: slaStatus || undefined,
          dateFrom: dateFrom || undefined,
          dateTo: dateTo || undefined,
        }
        const insightsPayload = await fetchTaskAnalyticsInsights(query)
        if (!active) return
        setInsights(insightsPayload || {})
      } catch (requestError) {
        if (!active) return
        setInsights(null)
        setError(extractApiErrorMessage(requestError, 'Failed to load task analytics insights.'))
      } finally {
        if (active) setIsLoadingInsights(false)
      }
    }
    loadInsights()
    return () => {
      active = false
    }
  }, [projectId, departmentId, userId, status, priority, slaStatus, dateFrom, dateTo])

  useEffect(() => {
    let active = true
    async function loadRiskList() {
      setIsLoadingRisk(true)
      try {
        const payload = await fetchTaskAnalyticsRiskList({
          projectId: projectId || undefined,
          departmentId: departmentId || undefined,
          userId: userId || undefined,
          status: status || undefined,
          priority: priority || undefined,
          slaStatus: slaStatus || undefined,
          dateFrom: dateFrom || undefined,
          dateTo: dateTo || undefined,
          page: riskPage,
          pageSize: riskPageSize,
        })
        if (!active) return
        setRiskRows(Array.isArray(payload?.items) ? payload.items : [])
        setRiskCounts(payload?.counts || {})
        setRiskTotal(Number(payload?.total || 0))
      } catch (requestError) {
        if (!active) return
        setRiskRows([])
        setRiskCounts({})
        setRiskTotal(0)
        setError(extractApiErrorMessage(requestError, 'Failed to load task risk list.'))
      } finally {
        if (active) setIsLoadingRisk(false)
      }
    }
    loadRiskList()
    return () => {
      active = false
    }
  }, [projectId, departmentId, userId, status, priority, slaStatus, dateFrom, dateTo, riskPage, riskPageSize])

  function queryParams(overrides = {}) {
    const periodFrom = drillPeriodFrom || dateFrom
    const periodTo = drillPeriodTo || dateTo
    return {
      projectId: projectId || undefined,
      departmentId: departmentId || undefined,
      userId: drillUserId || userId || undefined,
      status: drillStatus || status || undefined,
      priority: drillPriority || priority || undefined,
      slaStatus: slaStatus || undefined,
      dateFrom: periodFrom || undefined,
      dateTo: periodTo || undefined,
      ...overrides,
    }
  }

  async function openDrilldown(
    title = 'Task Details',
    level = 'task',
    periodFrom = null,
    periodTo = null,
    extra = {},
  ) {
    setIsDetailsOpen(true)
    setDrillTitle(title)
    setDrillLevel(level)
    setDrillPeriodFrom(periodFrom)
    setDrillPeriodTo(periodTo)
    setDrillStatus(extra.status || null)
    setDrillUserId(extra.userId || null)
    setDrillPriority(extra.priority || null)
    setIsLoadingDrilldown(true)
    setDrilldownError('')
    try {
      const periodOverrides = periodFrom && periodTo ? { dateFrom: periodFrom, dateTo: periodTo } : {}
      setDrilldown(
        await fetchTaskAnalyticsDrilldown(
          queryParams({
            level,
            ...periodOverrides,
            ...extra,
          }),
        ),
      )
    } catch (requestError) {
      setDrilldown({ columns: [], rows: [] })
      setDrilldownError(extractApiErrorMessage(requestError, 'Failed to load drill-down data.'))
    } finally {
      setIsLoadingDrilldown(false)
    }
  }

  async function exportCurrentDrilldown(format = 'csv') {
    setIsExporting(true)
    try {
      const blob = await downloadTaskAnalyticsExport(queryParams({ level: drillLevel, format }))
      const url = window.URL.createObjectURL(blob)
      const anchor = document.createElement('a')
      anchor.href = url
      anchor.download = `task_analytics_${drillLevel}.${format}`
      anchor.click()
      window.URL.revokeObjectURL(url)
    } catch (requestError) {
      setDrilldownError(extractApiErrorMessage(requestError, 'Failed to export task analytics.'))
    } finally {
      setIsExporting(false)
    }
  }

  async function openTeam() {
    setIsTeamOpen(true)
    setIsLoadingTeam(true)
    setTeamError('')
    try {
      const payload = await fetchTaskAnalyticsTeamLoad({
        projectId: projectId || undefined,
        departmentId: departmentId || undefined,
        userId: userId || undefined,
        status: status || undefined,
        priority: priority || undefined,
        slaStatus: slaStatus || undefined,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
      })
      setTeamLoad(payload || {})
    } catch (requestError) {
      setTeamLoad(null)
      setTeamError(extractApiErrorMessage(requestError, 'Failed to load team breakdown.'))
    } finally {
      setIsLoadingTeam(false)
    }
  }

  async function exportRisk() {
    setIsExporting(true)
    try {
      const payload = await fetchTaskAnalyticsRiskExport({
        projectId: projectId || undefined,
        departmentId: departmentId || undefined,
        userId: userId || undefined,
        status: status || undefined,
        priority: priority || undefined,
        slaStatus: slaStatus || undefined,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
      })
      const columns = Array.isArray(payload?.columns) ? payload.columns : []
      const dataRows = Array.isArray(payload?.rows) ? payload.rows : []
      const csvRows = [
        columns,
        ...dataRows.map((row) =>
          columns.map((key) => {
            if (key === 'assignees') {
              return (row?.assignees || []).map((item) => item.name).join(' | ')
            }
            return row?.[key]
          }),
        ),
      ]
      downloadCsv(`task-risk-export-${todayIso()}.csv`, csvRows)
    } catch (requestError) {
      setError(extractApiErrorMessage(requestError, 'Failed to export task risk log.'))
    } finally {
      setIsExporting(false)
    }
  }

  function applyPreset(daysValue) {
    const parsed = Number(daysValue || 30)
    const normalizedDays = Number.isFinite(parsed) && parsed > 0 ? parsed : 30
    setPresetDays(String(normalizedDays))
    setDateTo(todayIso())
    setDateFrom(daysAgoIso(normalizedDays - 1))
  }

  const projectOptions = useMemo(() => toDropdownOptions(projects, 'All Projects'), [projects])
  const departmentOptions = useMemo(() => toDropdownOptions(departments, 'All Departments'), [departments])
  const userOptions = useMemo(() => toDropdownOptions(users, 'All Users'), [users])
  const statusOptions = useMemo(() => toValueOptions(statuses, 'All Statuses'), [statuses])
  const priorityOptions = useMemo(() => toValueOptions(priorities, 'All Priorities'), [priorities])
  const slaStatusOptions = useMemo(() => toValueOptions(slaStatuses, 'All SLA States'), [slaStatuses])
  const presetOptions = useMemo(
    () =>
      (datePresets.length ? datePresets : [{ label: 'Last 30 Days', days: 30 }]).map((item) => ({
        value: String(item.days),
        label: item.label,
      })),
    [datePresets],
  )

  return {
    state: {
      projectId,
      departmentId,
      userId,
      status,
      priority,
      slaStatus,
      presetDays,
      dateFrom,
      dateTo,
      projectOptions,
      departmentOptions,
      userOptions,
      statusOptions,
      priorityOptions,
      slaStatusOptions,
      presetOptions,
      insights,
      riskRows,
      riskCounts,
      riskPage,
      riskPageSize,
      riskTotal,
      teamLoad,
      isTeamOpen,
      isLoadingTeam,
      isExporting,
      isLoadingRisk,
      delayTrendView,
      taskLoadView,
      isDetailsOpen,
      drillTitle,
      drillLevel,
      drillPeriodFrom,
      drillPeriodTo,
      drillStatus,
      drillUserId,
      drillPriority,
      drilldown,
      isLoadingDrilldown,
      drilldownError,
      isLoading: isLoadingFilters || isLoadingInsights,
      isLoadingFilters,
      isLoadingInsights,
      error,
      teamError,
    },
    actions: {
      setRiskPage,
      setProjectId,
      setDepartmentId,
      setUserId,
      setStatus,
      setPriority,
      setSlaStatus,
      setDateFrom,
      setDateTo,
      applyPreset,
      setDelayTrendView,
      setTaskLoadView,
      openTeam,
      closeTeam: () => setIsTeamOpen(false),
      exportRisk,
      openDetails: openDrilldown,
      openTasks: (periodFrom = null, periodTo = null, status = null) =>
        openDrilldown(status ? `${status} Tasks` : 'Task Activity', 'task', periodFrom, periodTo, { status }),
      openProjects: (periodFrom = null, periodTo = null) =>
        openDrilldown('Projects Overview', 'project', periodFrom, periodTo),
      openUsers: (periodFrom = null, periodTo = null, userId = null) =>
        openDrilldown('Assignee Task Load', 'user', periodFrom, periodTo, { userId }),
      openDelayTrend: (periodFrom = null, periodTo = null) =>
        openDrilldown('Delayed Tasks Breakdown', 'delay', periodFrom, periodTo),
      openSlaRisks: (periodFrom = null, periodTo = null) =>
        openDrilldown('SLA Risk Register', 'sla', periodFrom, periodTo),
      openHistory: (periodFrom = null, periodTo = null) =>
        openDrilldown('Task Status & Activity History', 'history', periodFrom, periodTo),
      closeDetails: () => {
        setIsDetailsOpen(false)
        setDrillPeriodFrom(null)
        setDrillPeriodTo(null)
        setDrillStatus(null)
        setDrillUserId(null)
        setDrillPriority(null)
      },
      exportCurrentDrilldown,
      resetFilters: () => {
        setProjectId('')
        setDepartmentId('')
        setUserId('')
        setStatus('')
        setPriority('')
        setSlaStatus('')
        applyPreset('30')
        setIsDetailsOpen(false)
        setDrillPeriodFrom(null)
        setDrillPeriodTo(null)
        setDrillStatus(null)
        setDrillUserId(null)
        setDrillPriority(null)
      },
    },
  }
}
